if (!customElements.get("hero-slider")) {
  class HeroSlider extends SwiperComponent {
    constructor() {
      super();
      this.lastActiveIndex = null;
    }

    connectedCallback() {
      this.swiperOptions = this.buildHeroSliderOptions();
      this.swiperInitialized = false;
      this.section = this.closest("section");

      const inShopifySection =
        this.section && this.section.classList.contains("shopify-section");

      if (inShopifySection) {
        this.scheduleInit();
      }

      if (Shopify.designMode) {
        window.addEventListener("shopify:section:load", () => this.scheduleInit());
        window.addEventListener("shopify:section:select", () => this.scheduleInit());

        setTimeout(() => {
          if (!this.swiperInitialized) this.scheduleInit();
        }, 100);
      } else if (!inShopifySection) {
        window.addEventListener("load", () => this.scheduleInit());
      }

      /* Hero-specific event listeners */
      window.addEventListener("shopify:block:select", (e) => {
        if (this.swiper && e.target.closest("hero-slider") === this) {
          const selectedSlideIndex = +e.target.dataset.index;
          this.swiper.slideTo(selectedSlideIndex, 600);
        }
      });

      this.addKeyboardNavigation();
    }

    buildHeroSliderOptions() {
      const autoplayOptions = {
        delay: this.dataset.autoplayInterval || 5000,
      };

      const options = {
        rewind: true,
        slidesPerView: 1,
        speed: 600,
        followFinger: false,
        navigation: {
          nextEl: ".swiper-button--next",
          prevEl: ".swiper-button--prev",
        },
        autoplay: this.dataset.autoplay === "true" ? autoplayOptions : false,
        on: {
          init: this.handleSlideChange.bind(this),
          slideChange: this.handleSlideChange.bind(this),
        },
      };

      if (this.dataset.pagination === "true") {
        options.pagination = {
          el: ".swiper-pagination",
          clickable: true,
          renderBullet: function (i, className) {
            return `
              <button class="${className}">
                <span>0${i + 1}</span>
                <svg class="square-progress" width="26" height="26">
                  <rect class="square-origin" width="26" height="26" rx="5" ry="5" />
                </svg>
                <svg class="progress" width="1.8rem" height="1.8rem" style="inset-inline-start: ${0 - (i * 2.8 + 4.2)}rem">
                  <circle class="circle-origin" r="0.8rem" cx="0.95rem" cy="0.95rem"></circle>
                </svg>
              </button>
            `;
          },
        };
      }

      return options;
    }

    init() {
      if (this.swiperInitialized) return;
      this.swiperInitialized = true;

      // Use custom Swiper options for HeroSlider
      this.swiper = new Swiper(this, this.swiperOptions);
      this.moveHeroVideoPlayPauseIntoPagination();
      this.syncHeroVideoPlayPauseVisibility();

      // Maintain slider property for backward compatibility
      this.slider = this.swiper;

      window.themeSwiper?.notifyLayoutChange?.();
    }

    moveHeroVideoPlayPauseIntoPagination() {
      const host = this.querySelector(".js-hero-video-play-pause-host");
      const paginationEl = this.querySelector(
        ".swiper-pagination.hero__swiper-pagination",
      );
      if (!host) return;

      if (!paginationEl) {
        host.classList.add("hero__video-play-pause-host--standalone");
        host.removeAttribute("hidden");
        return;
      }

      while (host.firstChild) {
        paginationEl.appendChild(host.firstChild);
      }

      host.remove();
    }

    syncHeroVideoPlayPauseVisibility() {
      const slot = this.querySelector(".hero__video-play-pause-slot");
      if (!slot || !this.swiper) return;

      const slide = this.swiper.slides[this.swiper.activeIndex];
      const show =
        slide?.classList?.contains("hero__swiper-slide--has-video") ?? false;

      slot.classList.toggle("hero__video-play-pause--inactive", !show);
      slot.setAttribute("aria-hidden", show ? "false" : "true");

      const btn = slot.querySelector("video-play-pause-button");

      if (!btn) return;

      if (show) {
        btn.removeAttribute("disabled");
        btn.setAttribute("aria-disabled", "false");
        btn.setAttribute("tabindex", "0");
      } else {
        btn.setAttribute("disabled", "");
        btn.setAttribute("aria-disabled", "true");
        btn.setAttribute("tabindex", "-1");
      }
    }

    handleSlideChange(swiper) {
      this.syncHeroVideoPlayPauseVisibility();
      this.handleSlideChangeAnimations(swiper);

      /** change header menu color for active slide */
      const headerInner = document.querySelector(".header__inner");
      const heroInners = document.querySelectorAll(".hero__inner");
      const activeHeroInner = heroInners[swiper.activeIndex];

      if (!headerInner || !heroInners.length || !activeHeroInner) {
        return;
      }

      const menuColor = activeHeroInner.dataset.headerMenuTextColor;
      if (menuColor) {
        document.documentElement.style.setProperty(
          "--transparent-header-menu-text-color",
          menuColor,
        );
      }
    }
    /**
     * get direction & set custom transitions
     * with delay for content animation items
     * */
    handleSlideChangeAnimations(swiper) {
      let delay = 300;
      const queryElement = `[data-index="${swiper.activeIndex}"]`;
      const activeSlide = swiper.wrapperEl?.querySelector(queryElement);
      if (!activeSlide) return;
      const queryArray = [
        ...(activeSlide.querySelectorAll(".hero__animation") || []),
      ];
      if (!queryArray.length) {
        return;
      }
      const allowMobileAnimation =
        swiper.wrapperEl.closest(".hero__content")?.dataset.animationMobile ===
        "true";
      if (!allowMobileAnimation && DeviceDetector.isMobile()) {
        queryArray.forEach((element) => {
          element.classList.remove("in-delay");
        });
        return;
      }
      // set all element in delay
      queryArray.forEach((element) => {
        element.classList.add("in-delay");
      });

      // get direction and set transitions
      if (this.lastActiveIndex > swiper.activeIndex) {
        delay = 650 + delay;
        swiper.wrapperEl.style.transitionDuration = "1000ms";
        swiper.wrapperEl.style.transitionTimingFunction =
          "cubic-bezier(0.45, 0.00, 0.15, 0.95)";
      } else if (this.lastActiveIndex < swiper.activeIndex) {
        delay = 1000 + delay;
        swiper.wrapperEl.style.transitionDuration = "1000ms";
        swiper.wrapperEl.style.transitionTimingFunction =
          "cubic-bezier(0.45, 0.00, 0.15, 0.95)";
      }

      // set delay calculated per item
      setTimeout(() => {
        if (this.lastActiveIndex > swiper.activeIndex) {
          delay = 325 + delay;
        } else if (this.lastActiveIndex < swiper.activeIndex) {
          delay = 500 + delay;
        }
        queryArray.forEach((element, index) => {
          if (index === 1) {
            delay = delay + 50;
          } else if (index === 2) {
            delay = delay + 175;
          } else if (index === 3) {
            delay = delay + 300;
          }
          element.classList.remove("in-delay");
        });
      }, delay);

      // set last active global
      this.lastActiveIndex = swiper.activeIndex;
    }
    addKeyboardNavigation() {
      document.addEventListener("keydown", (event) => {
        if (this.isInViewport()) {
          if (event.key === "ArrowRight") {
            const nextButton = this.querySelector(".swiper-button--next");
            if (nextButton) {
              nextButton.click();
            }
          }
          if (event.key === "ArrowLeft") {
            const prevButton = this.querySelector(".swiper-button--prev");
            if (prevButton) {
              prevButton.click();
            }
          }
        }
      });
    }

    isInViewport() {
      const rect = this.getBoundingClientRect();
      return (
        rect.top < window.innerHeight && // top part in viewport
        rect.bottom > 0 && // bottom part in viewport
        rect.left < window.innerWidth && // left part in viewport
        rect.right > 0 // right part in viewport
      );
    }
  }
  customElements.define("hero-slider", HeroSlider);
}
